import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * Невидимая Яндекс SmartCaptcha.
 *
 * Используется на формах, которые дёргают V1 `${city}/VerificationPhone`
 * (отправка SMS-кода на сайте). Бэк принимает токен в параметре `smart-token`
 * и валидирует его. Приложение сюда не ходит, поэтому капча только на вебе.
 *
 * Компонент рендерит скрытый контейнер и отдаёт императивный метод `execute()`,
 * возвращающий Promise<string|null>:
 *   - строка — токен капчи (пройдена автоматически или пользователь решил челлендж);
 *   - null   — капча не настроена / не загрузилась / пользователь закрыл челлендж.
 * null трактуется вызывающим кодом как "без токена" (бэк в этом случае
 * пропускает запрос — fail-open, ничего не ломаем).
 */

const SITEKEY = process.env.REACT_APP_YANDEX_CAPTCHA_SITEKEY;
const SCRIPT_SRC = 'https://smartcaptcha.yandexcloud.net/captcha.js';

let scriptPromise = null;

function loadCaptchaScript() {
    if (typeof window !== 'undefined' && window.smartCaptcha) {
        return Promise.resolve();
    }
    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
        }

        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return scriptPromise;
}

const SmartCaptcha = forwardRef((props, ref) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const resolverRef = useRef(null);

    const settle = (value) => {
        if (resolverRef.current) {
            resolverRef.current(value);
            resolverRef.current = null;
        }
    };

    useEffect(() => {
        if (!SITEKEY) {
            // Ключ не задан — капча просто отключена, форма работает как раньше.
            // eslint-disable-next-line no-console
            console.warn('REACT_APP_YANDEX_CAPTCHA_SITEKEY не задан — капча отключена');
            return undefined;
        }

        let cancelled = false;

        loadCaptchaScript()
            .then(() => {
                if (cancelled || !window.smartCaptcha || !containerRef.current) {
                    return;
                }

                widgetIdRef.current = window.smartCaptcha.render(containerRef.current, {
                    sitekey: SITEKEY,
                    invisible: true,
                    hideShield: false,
                    callback: (token) => settle(token),
                });

                // Пользователь закрыл окно капчи, не решив — разблокируем ожидание.
                window.smartCaptcha.subscribe(widgetIdRef.current, 'challenge-hidden', () => settle(null));
                // Токен протух до отправки — считаем, что токена нет.
                window.smartCaptcha.subscribe(widgetIdRef.current, 'token-expired', () => settle(null));
                // Ошибка сети самой капчи — не блокируем пользователя.
                window.smartCaptcha.subscribe(widgetIdRef.current, 'network-error', () => settle(null));
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Не удалось загрузить капчу:', error);
            });

        return () => {
            cancelled = true;
            settle(null);
            if (widgetIdRef.current != null && window.smartCaptcha && window.smartCaptcha.destroy) {
                window.smartCaptcha.destroy(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    useImperativeHandle(ref, () => ({
        execute: () => {
            if (!SITEKEY || widgetIdRef.current == null || !window.smartCaptcha) {
                return Promise.resolve(null);
            }

            return new Promise((resolve) => {
                // Токен одноразовый — перед новым запуском сбрасываем прошлый.
                settle(null);
                resolverRef.current = resolve;
                window.smartCaptcha.reset(widgetIdRef.current);
                window.smartCaptcha.execute(widgetIdRef.current);
            });
        },
    }));

    return <div ref={containerRef} />;
});

export default SmartCaptcha;
